import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { config } from "../../config";
import { tvUploadRequest } from "../../services/tvUploadRequest";
import YouTubeProgrammeManager from "../../components/common/YouTubeProgrammeManager";
import "../../styles/youtube-programme.css";

export default function GestionTV() {
    const API_BASE = config.apiBase;
    const VIDEO_BASE_URL = `${config.uploadsBase}/videos`;
    const CHUNK_SIZE = config.chunkSize;

    const [videos, setVideos] = useState([]);
    const [file, setFile] = useState(null);
    const [titre, setTitre] = useState("");
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [deleting, setDeleting] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [selectedVideo, setSelectedVideo] = useState(null); // Pour la lecture modale

    const fileInputRef = useRef(null);
    const abortControllerRef = useRef(null);

    const safeFetch = tvUploadRequest;

    const fetchVideos = async () => {
        setLoading(true);
        try {
            const data = await safeFetch(`${API_BASE}/get_videos.php`);
            if (data.success) {
                setVideos(Array.isArray(data.videos) ? data.videos : []);
            } else {
                toast.error(data.message || "Erreur de chargement");
                setVideos([]);
            }
        } catch (err) {
            console.error(err);
            toast.error(err.message || "Erreur de connexion au serveur");
            setVideos([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVideos();
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            const url = URL.createObjectURL(selectedFile);
            setPreviewUrl(url);
            setUploadProgress(0);
        }
    };

    const uploadChunk = async (chunk, chunkIndex, totalChunks, uploadId, filename, fileSize) => {
        const formData = new FormData();
        formData.append("chunk", chunk);
        formData.append("upload_id", uploadId);
        formData.append("chunk_index", chunkIndex);
        formData.append("total_chunks", totalChunks);
        formData.append("filename", filename);
        formData.append("file_size", fileSize);

        return await safeFetch(`${API_BASE}/chunk_upload.php`, {
            method: "POST",
            body: formData,
            signal: abortControllerRef.current?.signal,
        });
    };

    const handleUpload = async () => {
        if (!file) {
            toast.error("Veuillez sélectionner une vidéo");
            return;
        }
        if (!titre.trim()) {
            toast.error("Veuillez saisir un titre");
            return;
        }

        setUploading(true);
        setUploadProgress(0);
        abortControllerRef.current = new AbortController();

        try {
            const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
            
            const initData = await safeFetch(`${API_BASE}/chunk_upload_init.php`, {
                method: "POST",
                signal: abortControllerRef.current.signal,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    filename: file.name,
                    total_chunks: totalChunks,
                    file_size: file.size
                })
            });
            if (!initData.success) throw new Error(initData.message || "Échec de l'initialisation");
            
            const uploadId = initData.upload_id;

            for (let i = 0; i < totalChunks; i++) {
                const start = i * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, file.size);
                const chunk = file.slice(start, end);
                
                await uploadChunk(chunk, i, totalChunks, uploadId, file.name, file.size);
                setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
            }

            const completeData = await safeFetch(`${API_BASE}/chunk_upload_complete.php`, {
                method: "POST",
                signal: abortControllerRef.current.signal,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ upload_id: uploadId, titre: titre })
            });

            if (completeData.success) {
                toast.success("Vidéo téléversée avec succès");
                setFile(null);
                setTitre("");
                setPreviewUrl(null);
                setUploadProgress(0);
                if (fileInputRef.current) fileInputRef.current.value = "";
                fetchVideos();
            } else {
                throw new Error(completeData.message || "Échec de la finalisation");
            }
        } catch (err) {
            if (err.name !== 'AbortError') {
                console.error(err);
                toast.error(err.message || "Erreur lors du téléversement");
            }
        } finally {
            setUploading(false);
            abortControllerRef.current = null;
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Supprimer cette vidéo ?")) return;
        setDeleting(id);
        try {
            const data = await safeFetch(`${API_BASE}/delete_video.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id }),
            });
            if (data.success) {
                toast.success("Vidéo supprimée");
                fetchVideos();
            } else {
                toast.error(data.message || "Erreur");
            }
        } catch (err) {
            toast.error("Erreur");
        } finally {
            setDeleting(null);
        }
    };

    const getVideoUrl = (video) => {
        if (!video) return "";
        return config.normalizeVideo(video.url || video.fichier);
    };

    const formatFileSize = (size) => {
        if (!size) return "";
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
        return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('fr-FR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        }).format(date);
    };

    // ✅ Ouvrir la modale de lecture
    const openPlayer = (video) => {
        setSelectedVideo(video);
    };

    // ✅ Fermer la modale
    const closePlayer = () => {
        setSelectedVideo(null);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
            <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
                {/* Header (inchangé) */}
                <div className="mb-8 overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 shadow-xl">
                    <div className="relative px-6 py-10 sm:px-10 sm:py-12">
                        <div className="absolute right-0 top-0 h-64 w-64 rounded-full bg-blue-500/10 blur-3xl"></div>
                        <div className="absolute bottom-0 left-0 h-48 w-48 rounded-full bg-purple-500/10 blur-3xl"></div>
                        
                        <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 backdrop-blur-sm">
                                    <span className="relative flex h-2 w-2">
                                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75"></span>
                                        <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500"></span>
                                    </span>
                                    <span className="text-xs font-semibold uppercase tracking-wide text-green-300">
                                        Diffusion en direct
                                    </span>
                                </div>
                                <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                                    Gestion NDTV
                                </h1>
                                <p className="mt-3 max-w-2xl text-sm text-slate-300 sm:text-base">
                                    Gérez votre bibliothèque vidéo.
                                </p>
                            </div>
                            
                            <div className="flex gap-3">
                                <div className="rounded-xl bg-white/10 px-6 py-4 text-center backdrop-blur-sm">
                                    <p className="text-xs font-medium uppercase tracking-wide text-slate-300">
                                        Total vidéos
                                    </p>
                                    <p className="mt-1 text-3xl font-bold text-white">
                                        {videos.length}
                                    </p>
                                </div>
                                <button
                                    onClick={fetchVideos}
                                    className="group rounded-xl bg-white/10 px-6 py-4 text-left transition hover:bg-white/20"
                                >
                                    <p className="text-xs font-medium uppercase tracking-wide text-slate-300">
                                        Action
                                    </p>
                                    <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-white">
                                        Actualiser
                                        <svg className="h-4 w-4 transition group-hover:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                    </p>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid gap-8 lg:grid-cols-[1fr_420px]">
                    {/* Formulaire d'upload (inchangé) */}
                    <div className="rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-200/50">
                        <div className="border-b border-slate-100 px-6 py-5">
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-200">
                                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                    </svg>
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-slate-800">
                                        Ajouter une vidéo
                                    </h2>
                                    <p className="mt-0.5 text-sm text-slate-500">
                                        Téléversez une nouvelle vidéo
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="space-y-5">
                                <div>
                                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                                        Titre de la vidéo
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Ex: Journal du campus"
                                        value={titre}
                                        onChange={(e) => setTitre(e.target.value)}
                                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100"
                                    />
                                </div>

                                <div>
                                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                                        Fichier vidéo
                                    </label>
                                    <div
                                        onClick={() => fileInputRef.current?.click()}
                                        className="group relative cursor-pointer overflow-hidden rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 transition hover:border-blue-400 hover:bg-blue-50/30"
                                    >
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="video/*"
                                            onChange={handleFileChange}
                                            className="hidden"
                                        />
                                        
                                        <div className="px-6 py-8 text-center">
                                            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-xl bg-blue-100 text-blue-600 transition group-hover:scale-110">
                                                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                                </svg>
                                            </div>
                                            <p className="text-base font-semibold text-slate-800">
                                                {file ? file.name : "Cliquez pour sélectionner"}
                                            </p>
                                            <p className="mt-1 text-sm text-slate-500">
                                                {file ? formatFileSize(file.size) : "MP4, AVI, MOV, MKV"}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {uploading && (
                                    <div>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span>Téléversement...</span>
                                            <span>{uploadProgress}%</span>
                                        </div>
                                        <div className="w-full bg-slate-200 rounded-full h-2">
                                            <div 
                                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                                style={{ width: `${uploadProgress}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                )}

                                <button
                                    onClick={handleUpload}
                                    disabled={uploading}
                                    className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-slate-900 to-slate-800 px-6 py-4 font-semibold text-white shadow-lg shadow-slate-300 transition hover:-translate-y-0.5 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 opacity-0 transition group-hover:opacity-100"></div>
                                    <span className="relative flex items-center justify-center gap-3">
                                        {uploading ? (
                                            <>
                                                <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Téléversement ({uploadProgress}%)...
                                            </>
                                        ) : (
                                            <>
                                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                                </svg>
                                                Publier la vidéo
                                            </>
                                        )}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Aperçu (inchangé) */}
                    <div className="rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-200/50">
                        <div className="border-b border-slate-100 px-6 py-5">
                            <h3 className="flex items-center gap-2 text-lg font-bold text-slate-800">
                                <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                Aperçu
                            </h3>
                        </div>
                        <div className="p-6">
                            <div className="overflow-hidden rounded-xl bg-slate-900">
                                {previewUrl ? (
                                    <video controls className="h-56 w-full object-contain" src={previewUrl} />
                                ) : (
                                    <div className="flex h-56 flex-col items-center justify-center gap-3 px-6 text-center">
                                        <svg className="h-16 w-16 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        <p className="text-sm text-slate-400">Aucune vidéo sélectionnée</p>
                                    </div>
                                )}
                            </div>
                            <div className="mt-4 rounded-xl bg-slate-50 p-4">
                                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Informations</p>
                                <p className="mt-1 font-semibold text-slate-800">{titre || "Titre non défini"}</p>
                                {file && <p className="mt-1 text-xs text-slate-500">Taille: {formatFileSize(file.size)}</p>}
                            </div>
                        </div>
                    </div>
                </div>

                <YouTubeProgrammeManager />

                {/* Liste des vidéos */}
                <div className="mt-10">
                    <div className="mb-6 flex items-center justify-between">
                        <div>
                            <h2 className="text-2xl font-bold text-slate-800">Bibliothèque vidéo</h2>
                            <p className="mt-1 text-sm text-slate-500">Gérez vos contenus diffusés sur NDTV</p>
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex min-h-[400px] items-center justify-center rounded-2xl border border-slate-200 bg-white">
                            <div className="text-center">
                                <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600"></div>
                                <p className="text-slate-500">Chargement...</p>
                            </div>
                        </div>
                    ) : videos.length === 0 ? (
                        <div className="flex min-h-[400px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 bg-white p-8 text-center">
                            <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-2xl bg-slate-100">
                                <svg className="h-10 w-10 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-slate-800">Aucune vidéo disponible</h3>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            {videos.map((video) => (
                                <div key={video.id} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-md transition hover:-translate-y-1 hover:shadow-xl">
                                    <div className="relative bg-slate-900 cursor-pointer" onClick={() => openPlayer(video)}>
                                        <video
                                            controls
                                            muted
                                            preload="metadata"
                                            playsInline
                                            className="aspect-video w-full bg-black object-contain"
                                            src={getVideoUrl(video)}
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <svg className="w-16 h-16 text-white/90" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M8 5v14l11-7z" />
                                            </svg>
                                        </div>
                                        <div className="absolute left-3 top-3 rounded-full bg-black/60 px-2 py-1 text-xs font-semibold text-white backdrop-blur">NDTV</div>
                                        {deleting === video.id && (
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                                                <div className="h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-4">
                                        <h3 className="line-clamp-2 font-bold text-slate-800">{video.titre}</h3>
                                        {video.date_ajout && <p className="mt-1 text-xs text-slate-500">{formatDate(video.date_ajout)}</p>}
                                        <div className="mt-4 flex gap-2">
                                            <button
                                                onClick={() => openPlayer(video)}
                                                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
                                            >
                                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                Lire
                                            </button>
                                            <button onClick={() => handleDelete(video.id)} disabled={deleting === video.id} className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700 transition hover:bg-red-600 hover:text-white disabled:opacity-50">
                                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                                Supprimer
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* ✅ Modale de lecture vidéo */}
            {selectedVideo && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={closePlayer}>
                    <div className="relative bg-black rounded-2xl max-w-4xl w-full overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={closePlayer}
                            className="absolute top-3 right-3 z-10 rounded-full bg-black/60 p-2 text-white hover:bg-black/80 transition"
                        >
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <div className="aspect-video">
                            <video
                                controls
                                autoPlay
                                className="w-full h-full"
                                src={getVideoUrl(selectedVideo)}
                            />
                        </div>
                        <div className="p-4 bg-white dark:bg-slate-900">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white">{selectedVideo.titre}</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Ajouté le {formatDate(selectedVideo.date_ajout)}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
